import { getCookie } from "cookies-next";
import { useTranslations } from "next-intl";
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";

import InputBase from "@/components/ui/Form/Input/InputBase";
import SelectBase, { SelectOptions } from "@/components/ui/Form/Select/SelectBase";
import { TelephoneProps } from "@/types";
import { countryCodeLeadPrefixes, countryCodePrefixes, countryNamePrefixes, prefixes } from "@/utils/data/prefixes";

const selectArrowStandard = `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"  width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.8327 7.5L9.99935 13.3333L4.16602 7.5" stroke="%23222222" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>')`;
const selectArrowReverse = `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"  width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.8327 7.5L9.99935 13.3333L4.16602 7.5" stroke="%23ffffff" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>')`;
const defaultValue = {
  name: "United Arab Emirates",
  code: "AE",
  dial_code: "+971",
};
const TelephoneBase: FC<TelephoneProps> = ({
  prefixName,
  phoneName,
  reverse,
  prefixField,
  prefixState,
  phoneField,
  phoneState,
  onNumberChange,
  onPrefixChange,
  onCountryChange,
  error,
  errorMessage,
  onClearField,
  phoneValue, // TODO da capire se rimuovere
  controlledCountryCode,
  defaultNumber, // TODO da capire se rimuovere
  onBlur,
  showCountryName,
  showCountryCode,
  showCountryCodeLeadPrefixes,
  positionSearchable,
}) => {
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>(
    (controlledCountryCode || getCookie("country")?.toLocaleUpperCase()) ?? defaultValue.code,
  );
  const flag = useMemo(() => `url('/icons/${selectedCountry.toLowerCase()}.png')`, [selectedCountry]);
  const [numberError, setNumberError] = useState(false);

  const numberChangeHandler = (e: any) => {
    let prefix = prefixField?.value ? prefixField?.value.split("_").find((i: string) => i[0] == "+") : "+971";
    if (prefix) {
      const hasSpaces = /\s/.test(prefix + e);

      if (hasSpaces) {
        setNumberError(true); // Set an error because spaces are not allowed
      } else {
        setNumberError(!isValidPhoneNumber(prefix + e));
      }
    }
  };

  const prefixChangeHandler = (e: any) => {
    let prefix = e ? e.split("_").find((i: string) => i[0] == "+") : "+971";
    let number = phoneField?.value;
    if (number) {
      const hasSpaces = /\s/.test(prefix + number);

      if (hasSpaces) {
        setNumberError(true); // Set an error because spaces are not allowed
      } else {
        setNumberError(!isValidPhoneNumber(prefix + number));
      }
    }
  };

  useEffect(() => {
    if (controlledCountryCode != selectedCountry) {
      onCountryChange?.(selectedCountry);
    }
  }, [controlledCountryCode, onCountryChange, selectedCountry]);

  let currentPrefixes = prefixes;
  if (showCountryCode) {
    currentPrefixes = countryCodePrefixes(selectedCountry);
  }
  if (showCountryName) {
    currentPrefixes = countryNamePrefixes(selectedCountry);
  }
  if (showCountryCodeLeadPrefixes) {
    currentPrefixes = countryCodeLeadPrefixes(selectedCountry);
  }

  return (
    <div className="flex items-end gap-m">
      <SelectBase
        label={t("label.common.businessNumber")}
        name={prefixName}
        options={currentPrefixes as SelectOptions[]}
        field={prefixField}
        fieldState={prefixState}
        selectClassName={`${selectedCountry !== "" ? "pl-xl" : ""} ${prefixState?.error ? "border-b !border-[#B42318] text-[#B42318]" : "border-b border-b-gray-10"} bg-no-repeat`}
        selectStyle={{
          // backgroundImage: `${reverse ? selectArrowReverse : selectArrowStandard}, ${flag}`,
          backgroundImage: `${reverse ? selectArrowReverse : selectArrowStandard}, ${selectedCountry ? flag : "none"}`,
          backgroundSize: "20px, 20px",
          backgroundPosition: "right center, 4px center",
          backgroundRepeat: "no-repeat",
          paddingLeft: selectedCountry ? "32px" : "12px",
        }}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          console.log("eeee", e);

          const country = currentPrefixes.find((item) => item.value === e.currentTarget.value);
          setSelectedCountry(country?.country ?? "");
          onPrefixChange?.(country?.value ?? "");
          prefixField?.onChange(e);
          prefixChangeHandler(e.target.value);
        }}
        reverse={reverse}
        required
        // className="w-[20%]  md:min-w-[130px]"
        className="w-[40%] min-w-[90px]"
        searchable={true}
        positionSearchable={positionSearchable || "fixed"}
      />
      <div className="flex-1 md:w-[-webkit-fill-available]">
        <InputBase
          type="tel"
          // pattern="\d{6,10}"
          // pattern="^\S*$"
          // maxLength={10}
          field={phoneField}
          fieldState={phoneState}
          name={phoneName}
          ref={inputRef}
          reverse={reverse}
          required
          onChange={(e) => {
            phoneField?.onChange(e);
            onNumberChange?.(e.target.value);
            numberChangeHandler(e.target.value);
          }}
          error={error || numberError}
          onClearField={onClearField}
          errorMessage={errorMessage}
          value={phoneValue}
          onBlur={onBlur}
        />
      </div>
    </div>
  );
};
export default TelephoneBase;
